import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, MapPin, Link2, Clock, CalendarClock, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionContextSelector from './ConstructionContextSelector';
import ConstructionBreadcrumb from './ConstructionBreadcrumb';
import ProgressCaptureModal from './ProgressCaptureModal';
import EvidenceButton from './sitewalk/EvidenceButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const dt = (v) => (v ? String(v).slice(0, 10) : '—');
const STATUS_BADGE = {
  NOT_STARTED: 'bg-slate-100 text-slate-600', IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700', ON_HOLD: 'bg-amber-100 text-amber-700', UNKNOWN: 'bg-slate-100 text-slate-500',
};
const EVIDENCE_LABEL = { job: 'Analysis / Measurement', upload: 'Site Upload', issue: 'Issue', observation: 'Quantity Observation', drawing: 'Drawing' };

// Slide-over: activity fields, ConstructionContext mapping, BOQ/drawing links (with Phase-2 quantities,
// shown factually — never converted to a progress %), and a chronological evidence timeline.
const ActivityDetailDrawer = ({ projectId, activityId, canMap, canLinkEvidence, onClose, onChanged }) => {
  const [activity, setActivity] = useState(null);
  const [boqDetails, setBoqDetails] = useState({});
  const [ctx, setCtx] = useState({});
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [showCapture, setShowCapture] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const a = await axios.get(`${API}/projects/${projectId}/activities/${activityId}`).then((r) => r.data);
      setActivity(a); setCtx(a.constructionContext || {});
      axios.get(`${API}/projects/${projectId}/activities/${activityId}/progress`).then((r) => setProgress(r.data)).catch(() => setProgress(null));
      const boqLinks = (a.links || []).filter((l) => l.targetType === 'boqItem');
      const details = {};
      await Promise.all(boqLinks.map(async (l) => {
        try {
          const [it, rc] = await Promise.all([
            axios.get(`${API}/projects/${projectId}/boq-items/${l.targetId}`).then((r) => r.data),
            axios.get(`${API}/projects/${projectId}/boq-items/${l.targetId}/reconcile`).then((r) => r.data).catch(() => null),
          ]);
          details[l.targetId] = { item: it, recon: rc };
        } catch (e) { /* skip */ }
      }));
      setBoqDetails(details);
    } catch (e) { toast.error('Failed to load activity'); } finally { setLoading(false); }
  }, [projectId, activityId]);

  useEffect(() => { load(); }, [load]);

  const saveContext = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/activities/${activityId}/context`, { context: ctx });
      toast.success('Context mapped'); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Context mapping failed'); }
  };

  const fields = activity ? [
    ['Activity Code', activity.activityCode], ['WBS', activity.externalWbsCode || activity.wbsId],
    ['Planned Start', dt(activity.plannedStart)], ['Planned Finish', dt(activity.plannedFinish)],
    ['Actual Start', dt(activity.actualStart)], ['Actual Finish', dt(activity.actualFinish)],
    ['Original Duration', activity.originalDuration], ['Remaining Duration', activity.remainingDuration],
    ['Reported Progress', activity.reportedPercent != null ? `${activity.reportedPercent}%` : '—'],
    ['Schedule Version', activity.scheduleVersion],
  ] : [];

  const evidence = activity?.evidence || [];
  const drawingLinks = (activity?.links || []).filter((l) => l.targetType === 'drawing');
  const boqLinks = (activity?.links || []).filter((l) => l.targetType === 'boqItem');

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="activity-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-slate-50 h-full overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-slate-900 truncate flex items-center gap-2"><CalendarClock className="w-5 h-5 text-blue-600 shrink-0" />{activity ? `${activity.activityCode || 'Activity'} — ${activity.activityName || ''}` : 'Activity'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="activity-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        {loading || !activity ? (
          <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Fields */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[activity.status] || STATUS_BADGE.UNKNOWN}`} data-testid="activity-status-badge">{activity.status}</span>
                {activity.milestone && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Milestone</span>}
                <ConstructionBreadcrumb projectId={projectId} context={activity.constructionContext} fallback="Unmapped" testId="activity-ctx-crumb" />
                <div className="mt-2" data-testid="activity-360-evidence"><EvidenceButton projectId={projectId} kind="activity" id={activityId} compact /></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {fields.map(([label, val]) => (
                  <div key={label}><p className="text-xs text-slate-400">{label}</p><p className="font-medium text-slate-800">{fmt(val)}</p></div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-400">“Reported Progress” is the value from the imported schedule. Visual/AI progress is out of scope in Phase 3.</p>
            </div>

            {activity.source === 'P6_XER' && (
              <div className="bg-white rounded-xl border border-cyan-200 p-4" data-testid="activity-p6-provenance">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-semibold">Source: Primavera P6</span>
                  {activity.externalActivityId && <span className="text-xs text-slate-500">P6 Activity ID: <b>{activity.externalActivityId}</b></span>}
                  {activity.externalWbsCode && <span className="text-xs text-slate-500">P6 WBS: <b>{activity.externalWbsCode}</b></span>}
                  {activity.externalSyncState === 'EXTERNAL_MISSING' && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">External Missing since {dt(activity.externalMissingSince)}</span>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-xs text-slate-400">P6 Physical %</p><p className="font-medium text-slate-800">{activity.p6Progress?.physicalPercent ?? '—'}</p></div>
                  <div><p className="text-xs text-slate-400">P6 Duration %</p><p className="font-medium text-slate-800">{activity.p6Progress?.durationPercent ?? '—'}</p></div>
                  <div><p className="text-xs text-slate-400">P6 Units %</p><p className="font-medium text-slate-800">{activity.p6Progress?.unitsPercent ?? 'Not available'}</p></div>
                  <div><p className="text-xs text-slate-400">P6 Data Date (last seen)</p><p className="font-medium text-slate-800">{dt(activity.lastSeenDataDate)}</p></div>
                </div>
                <p className="mt-3 text-[11px] text-slate-400">P6 reported progress is planning-system data — it is <b>not</b> Reviewed Visual Progress. RVX-reviewed values are governed separately and are never overwritten by P6.</p>
              </div>
            )}

            {/* BOQ mappings (factual quantities) */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="activity-boq-links">
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><Boxes className="w-4 h-4" /> BOQ Mappings</h3>
              {boqLinks.length === 0 ? <p className="text-xs text-slate-400">No BOQ items mapped.</p> : boqLinks.map((l) => {
                const d = boqDetails[l.targetId];
                const it = d?.item; const rc = d?.recon;
                return (
                  <div key={l.id} className="border-t border-slate-50 py-2 text-sm" data-testid="activity-boq-row">
                    <p className="font-medium text-slate-800">{it ? `${it.itemCode || ''} ${it.description || ''}` : l.targetId.slice(0, 8)} <span className="text-[10px] text-slate-400">({l.status})</span></p>
                    {it && (
                      <div className="grid grid-cols-3 gap-2 mt-1 text-xs">
                        <div><span className="text-slate-400">Planned:</span> {fmt(it.plannedQuantity)} {it.unit || ''}</div>
                        <div><span className="text-slate-400">Reviewed evidence:</span> {rc ? fmt(rc.installed) : '—'}</div>
                        <div><span className="text-slate-400">Remaining:</span> {rc ? fmt(rc.remaining) : '—'}</div>
                      </div>
                    )}
                  </div>
                );
              })}
              {drawingLinks.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-50">
                  <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Link2 className="w-3 h-3" /> Drawings</p>
                  {drawingLinks.map((l) => <span key={l.id} className="text-xs text-slate-600 mr-2">{l.targetId.slice(0, 8)} ({l.status})</span>)}
                </div>
              )}
              <p className="mt-2 text-[11px] text-slate-400">Quantities are shown factually. ReasonVisionX does not derive an activity progress % from these in Phase 3.</p>
            </div>

            {/* Progress Intelligence (Phase 4) */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="activity-progress-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">Progress</h3>
                {canLinkEvidence && <button onClick={() => setShowCapture(true)} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg" data-testid="activity-capture-progress-btn">Capture Progress</button>}
              </div>
              {progress ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div><p className="text-xs text-slate-400">Planned</p><p className="font-semibold">{progress.plannedProgress != null ? `${progress.plannedProgress}%` : '—'}</p></div>
                    <div><p className="text-xs text-slate-400">Reported</p><p className="font-semibold">{progress.reportedProgress != null ? `${progress.reportedProgress}%` : '—'}</p></div>
                    <div><p className="text-xs text-slate-400">AI Observed</p><p className="font-semibold text-purple-700">{progress.aiObservedProgress != null ? `${progress.aiObservedProgress}%` : '—'}</p></div>
                    <div><p className="text-xs text-slate-400">Reviewed Visual</p><p className="font-semibold text-emerald-700">{progress.reviewedVisualProgress != null ? `${progress.reviewedVisualProgress}%` : '— No Evidence'}</p></div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Variance {progress.planVsReviewedVariancePp === null ? '—' : `${progress.planVsReviewedVariancePp} pp`} · Method {progress.progressMethod} · AI is a proposal, not certified truth.</p>
                </>
              ) : <p className="text-xs text-slate-400">Progress not available.</p>}
            </div>

            {/* Context mapping */}
            {canMap && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> Construction Context</h3>
                <ConstructionContextSelector projectId={projectId} value={ctx} onChange={setCtx} />
                <button onClick={saveContext} className="mt-3 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg" data-testid="activity-save-context">Save Context</button>
              </div>
            )}

            {/* Evidence timeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="activity-evidence-timeline">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Clock className="w-4 h-4" /> Evidence Timeline</h3>
              </div>
              {evidence.length === 0 ? <p className="text-sm text-slate-400" data-testid="activity-no-evidence">No Evidence linked yet. (This does not imply 0% progress.)</p> : (
                <div className="space-y-3">
                  {evidence.map((e) => (
                    <div key={e.id} className="border-l-2 border-slate-200 pl-3" data-testid="activity-evidence-row">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-400">{dt(e.createdAt)}</span>
                        <span className="text-sm font-medium">{EVIDENCE_LABEL[e.evidenceType] || e.evidenceType}</span>
                        {e.inheritedFrom && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">inherited</span>}
                      </div>
                      {e.evidenceId && <p className="text-xs text-slate-500 mt-0.5 font-mono">{e.evidenceId.slice(0, 12)}…</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showCapture && activity && (
        <ProgressCaptureModal projectId={projectId} activityId={activityId}
          onClose={() => setShowCapture(false)} onChanged={() => load()} onCreated={() => load()} />
      )}
    </div>
  );
};

export default ActivityDetailDrawer;
